/**
 * Minimal, dependency-free CSV codec (RFC 4180).
 *
 * Cells are quoted when they contain a comma, double-quote, CR or LF; embedded
 * double-quotes are escaped by doubling. Complex domain fields (assignment
 * arrays, action-count maps) are JSON-stringified into a single cell so a row
 * stays flat and the file round-trips losslessly — and remains importable into
 * the SQL schema in docs/DATABASE_SCHEMA.sql.
 */

function escapeCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Serialise rows (column → string) to a CSV document with a header line. */
export function toCsv(columns: string[], rows: Record<string, string>[]): string {
  const header = columns.map(escapeCell).join(",");
  const body = rows.map((row) =>
    columns.map((col) => escapeCell(row[col] ?? "")).join(","),
  );
  return [header, ...body].join("\r\n");
}

/**
 * Parse a CSV document into row objects keyed by the header columns. Handles
 * quoted fields containing commas, newlines and escaped quotes.
 */
export function fromCsv(text: string): Record<string, string>[] {
  const rows = parseRows(text);
  if (rows.length === 0) return [];
  const [header, ...data] = rows;
  return data
    // Skip fully-empty trailing lines.
    .filter((cells) => cells.length > 1 || (cells[0] ?? "") !== "")
    .map((cells) => {
      const obj: Record<string, string> = {};
      header.forEach((col, i) => {
        obj[col] = cells[i] ?? "";
      });
      return obj;
    });
}

/** Tokenise the whole document into rows of cells (state machine). */
function parseRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++; // consume escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (ch === "\r") {
      // handled by the \n branch; swallow lone CR
    } else {
      cell += ch;
    }
  }

  // Flush trailing cell/row (file may not end with newline).
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
